import React, { useState, useRef, useEffect } from "react";
import {
  Typography,
  FormControl,
  ButtonBase,
  LinearProgress,
  Button,
  Alert,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CachedIcon from "@mui/icons-material/Cached";
import uploadIcon from "../../../assests/logos/loader.svg";
import { UploadDropZoneProps, useIntl } from "./utils";
import { convertFileSizeToBytes } from "./utils";
import clsx from "clsx";
import useStyles from "./utils";

export const UploadDropZone = (props: UploadDropZoneProps) => {
  const {
    onClick,
    label,
    id,
    dataTestId,
    error,
    acceptFiles,
    maxSize,
    value,
    type,
    multiple,
    fileRange,
    disabled,
    removeText,
    manualError,
    acceptMultiDot = true,
    allowDuplication,
  } = props;

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [progressTimeoutId, setProgressTimeoutId] = useState<any>(null);
  const [customError, setCustomError] = useState<string>("");
  const [formatError, setFormatError] = useState<boolean>(false);
  const [textFileContent, setTextFileContent] = useState<string>("");

  const dropZoneRef = useRef<any>(null);
  const classes = useStyles();

  useEffect(() => {
    if (manualError) {
      setCustomError(manualError);
      setShowProgressBar(true);
    }
  }, [manualError]);

  useEffect(() => {
    const url = value?.file ? URL.createObjectURL(value.file) : null;
    setImageUrl(url);
  }, [value?.file]);

  useEffect(() => {
    if (value?.file?.type === "text/plain") {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const content = e.target.result;
        setTextFileContent(content);
      };
      reader.readAsText(value?.file);
    }
  }, [value?.file]);

  const fileValidation = (files: any, timeoutId: any) => {
    const oversizedFiles: string[] = [];
    let hasError = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const matchResult = maxSize.match(/(\d+)([A-Za-z]+)/);
      const [, size, unit]: any = matchResult;
      const maxFileSize = convertFileSizeToBytes(parseInt(size, 10), unit);
      const fileExtension = file?.name?.split(".").pop().toLowerCase();

      if (file.name.match(/.*\..*\..*/) && acceptMultiDot) {
        setCustomError(
          "Upload a valid file format. Avoid using multiple dots in the filename."
        );
        clearTimeout(timeoutId);
        return;
      }

      const isDuplicate = multiple
        ? value?.some(
            (uploadedFile: any) =>
              uploadedFile.name === file.name && uploadedFile.size === file.size
          )
        : false;

      if (isDuplicate && !allowDuplication) {
        clearTimeout(timeoutId);
        const error = useIntl["resource.error.duplicate"];
        setCustomError(error);
        return error;
      }

      if (!acceptFiles.includes(fileExtension)) {
        setFormatError(true);
        setShowProgressBar(false);
        clearTimeout(timeoutId);
        setCustomError("");
        return;
      } else {
        setCustomError("");
        setFormatError(false);
      }

      if (!multiple) {
        if (file.size >= maxFileSize) {
          setCustomError(useIntl["error.fileSize"].replace("{size}", maxSize));
          clearTimeout(timeoutId);
          return;
        }
      }

      if (multiple) {
        if (file.size >= maxFileSize) {
          oversizedFiles.push(file?.name);
          hasError.push(true);
        } else {
          hasError.push(false);
        }
      }

      if (multiple) {
        const combinedFilesLength = value
          ? value.length + files.length
          : files.length;

        if (fileRange && combinedFilesLength > fileRange) {
          setCustomError(
            `Files exceed the limit. Only ${fileRange} file(s) are allowed.`
          );
          clearTimeout(timeoutId);
          return;
        }
      }
    }

    if (hasError?.some((error) => error)) {
      const maxSizeError = useIntl["error.fileSize"]
        .replace("{size}", maxSize)
        ?.replace(
          "The file is",
          `The ${oversizedFiles?.join(", ")} ${
            oversizedFiles?.length > 1 ? "files are" : "file is"
          }`
        );
      setCustomError(maxSizeError);
      const filesArray = Array.from(files);
      const filteredFiles = filesArray.filter(
        (file: any) => !oversizedFiles.includes(file.name)
      );
      clearTimeout(timeoutId);
      const events: any = {
        target: {
          name: id,
          id,
          files: filteredFiles,
          error: maxSizeError,
        },
      };
      const hasLargeFile = hasError.every((error) => error);
      if (!hasLargeFile) onClick(events);
      return;
    }
  };

  const handleFileChange = (event: any) => {
    setFormatError(false);
    const file = event.target.files[0];
    if (file) {
      setShowProgressBar(true);

      const timeoutId = setTimeout(() => {
        setShowProgressBar(false);
        onClick(event);
      }, 1000);

      fileValidation(event.target.files, timeoutId);
      setProgressTimeoutId(timeoutId);
    }
  };

  const handleDropZoneKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      dropZoneRef.current?.click();
    }
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    setFormatError(false);

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const events: any = {
        target: {
          name: id,
          id,
          files: event.dataTransfer.files,
        },
      };

      setShowProgressBar(true);

      const timeoutId = setTimeout(() => {
        setShowProgressBar(false);
        onClick(events);
      }, 1000);

      fileValidation(event.dataTransfer.files, timeoutId);
      setProgressTimeoutId(timeoutId);
    }
  };

  const removeImage = () => {
    const events: any = {
      target: {
        name: id,
        id,
        files: {},
      },
    };
    onClick(events);
  };

  const cancelUpload = () => {
    if (progressTimeoutId !== null) {
      clearTimeout(progressTimeoutId);
    }
    setShowProgressBar(false);
  };

  const tryAgain = () => {
    cancelUpload();
    setCustomError("");
  };

  const convertExtensionsToMIME = (extensions: any) => {
    const mimeTypes: any = {
      txt: "text/plain",
      csv: "text/csv",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      gif: "image/gif",
      jpeg: "image/jpeg",
      jpg: "image/jpeg",
      mp4: "video/mp4",
      pdf: "application/pdf",
      png: "image/png",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      zip: "application/zip",
      jfif: "image/jpeg",
    };

    return extensions.map((ext: string) => mimeTypes[ext]).filter(Boolean);
  };

  return (
    <FormControl fullWidth disabled={disabled}>
      <div
        className={clsx({
          [classes.uploadedDropZone]: value?.file,
          [classes.uploadDropZone]: !value?.file,
          [classes.imageDisabled]: disabled,
        })}
        tabIndex={0}
        onKeyDown={value?.file ? undefined : handleDropZoneKeyDown}
        onDragOver={value?.file ? undefined : (e) => e.preventDefault()}
        onDrop={value?.file ? undefined : handleDrop}
        data-testid={dataTestId}
      >
        {showProgressBar ? (
          <div className={classes.progressDiv}>
            <LinearProgress
              value={customError ? 80 : 10}
              variant={customError ? "determinate" : "indeterminate"}
              className={customError ? classes.linearErrorProgress : classes.linearProgress}
            />
            {customError ? (
              <div className={classes.progressDiv}>
                <Typography className={classes.errorMsg}>{customError}</Typography>
                <Button className={classes.tryAgain} onClick={tryAgain}>
                  <CachedIcon className={classes.tryAgainIcon} />
                  {useIntl["file.tryAgain"]}
                </Button>
              </div>
            ) : (
              <Button className={classes.clearUpload} onClick={cancelUpload}>
                <ClearIcon className={classes.clearIcon} />
                {useIntl["file.cancelUpload"]}
              </Button>
            )}
          </div>
        ) : value?.file ? (
          <ButtonBase
            className={`${classes.image} ${classes.fullwidthButton}`}
            onClick={removeImage}
          >
            {value?.file?.type === "application/pdf" ? (
              imageUrl && <embed src={imageUrl} type="application/pdf" width="100%" />
            ) : ["image/png", "image/jpeg", "image/jpg"].includes(value?.file?.type) ? (
              <span
                className={classes.imageSrc}
                style={{ backgroundImage: `url(${imageUrl})` }}
              />
            ) : value?.file?.type === "text/plain" ? (
              <Typography className={classes.textFileArea}>{textFileContent}</Typography>
            ) : null}
          </ButtonBase>
        ) : (
          <label htmlFor={id} className={classes.fileInput} data-testid={dataTestId}>
            {formatError ? (
              <Alert severity="error" className={classes.uploadAlert}>
                <Typography>{useIntl["error.notSupportedFileTypes"]}</Typography>
              </Alert>
            ) : (
              <img src={uploadIcon} alt={id} draggable={false} />
            )}
            <Typography>{label}</Typography>
          </label>
        )}
        <input
          type="file"
          id={id}
          className={classes.input}
          data-testid={`${dataTestId}-input`}
          onChange={handleFileChange}
          ref={dropZoneRef}
          accept={convertExtensionsToMIME(acceptFiles).join(", ")}
          multiple={multiple}
          disabled={disabled}
        />
      </div>
    </FormControl>
  );
};